import { FunctionComponent, createContext, useMemo } from 'react';
import { ActorRefFrom, InspectionEvent, StateFrom } from 'xstate';
import { useActorRef } from '@xstate/react';
import { useLoadingManager } from '../../../LoadingManager';
import { useHasReviewedApplication } from '../../../../lib/hooks/useHasReviewedApplication';
import { useBuyerMachine } from '../../../BuyerMachineContext';
import { useAuthentication } from '../../../AuthenticationMachineContext';
import { getLoadingIndicatorActions } from '../../actions';
import {
  RouteMachineContextProps,
  RouteMachineServiceProps,
} from '../../RouteMachineService';
import { getDefaultApplicationErrorRouteGuards } from '../../guards/applicationMachineErrors';
import { getDefaultBuyerErrorRouteGuards } from '../../guards/buyerMachineErrors';
import { getRoutePathConditionGuards } from '../../guards/routePathConditions';
import { useApplicationMachineStateEventEmitter } from '../../hooks/useApplicationMachineStateEventEmitter';
import { useAuthenticationMachineStateEventEmitter } from '../../hooks/useAuthenticationMachineStateEventEmitter';
import { useBuyerApplicationEventEmitter } from '../../hooks/useBuyerApplicationEventEmitter';
import { useBuyerMachineStateEventEmitter } from '../../hooks/useBuyerMachineStateEventEmitter';
import { useHandleStateRouteTransition } from '../../hooks/useHandleStateRouteTransition';
import { useRouteTransitionActions } from '../../hooks/useRouteTransitionActions';
import { useSyncRouterStateEventEmitter } from '../../hooks/useSyncRouterStateEventEmitter';
import { useInspectLogoutRouterEvent } from '../../hooks/useInspectLogoutRouterEvent';
import { routingMachine } from './cartRoutingMachine';

export type CartRoutingMachineSnapshot = StateFrom<typeof routingMachine>;

type CartRoutingMachineActorRef = ActorRefFrom<typeof routingMachine>;

export const CartRoutingMachineContext =
  createContext<RouteMachineContextProps<CartRoutingMachineActorRef | null> | null>(
    null
  );

/**
 * Provider to support Cart routing
 * @param React.Children
 * @returns FunctionComponent<RouteMachineServiceProps>
 */
export const CartRoutingMachineProvider: FunctionComponent<RouteMachineServiceProps> =
  ({ children }) => {
    const { state: authState } = useAuthentication();
    const { state: buyerState } = useBuyerMachine();
    const { hasReviewedApplication } = useHasReviewedApplication();
    const { addLoader, removeLoader } = useLoadingManager();
    const inspectLogoutRouterEvent = useInspectLogoutRouterEvent();
    // Handle state route transitions
    const { isTransitioning, handleStateRouteTransition } =
      useHandleStateRouteTransition();

    const routingMachineService = useActorRef(
      routingMachine.provide({
        guards: {
          hasReviewedApplication: () => {
            if (authState.matches({ authenticated: 'anonymous' })) {
              return true;
            }
            return hasReviewedApplication;
          },
          isUnauthenticatedInitial: () => {
            return authState.matches({ unAuthenticated: 'initial' });
          },
          isBuyerReadyComplete: () => {
            return buyerState.matches({ ready: 'complete' });
          },
          isCartRetrieved: () => {
            return buyerState.matches({ ready: 'cartRetrieved' });
          },
          // Route path condition guards
          ...getRoutePathConditionGuards(),
          // Application Error condition routing guards
          ...getDefaultApplicationErrorRouteGuards(),
          // Buyer Error condition routing guards
          ...getDefaultBuyerErrorRouteGuards(),
        },
        actions: {
          ...getLoadingIndicatorActions({ addLoader, removeLoader }),
        },
      }),
      {
        inspect: (inspectionEvent: InspectionEvent) => {
          handleStateRouteTransition(inspectionEvent);
          inspectLogoutRouterEvent(inspectionEvent);
        },
      }
    );

    // Shared event emitters
    useSyncRouterStateEventEmitter(routingMachineService);
    useBuyerApplicationEventEmitter(routingMachineService);
    useApplicationMachineStateEventEmitter(routingMachineService);
    useBuyerMachineStateEventEmitter(routingMachineService);
    useAuthenticationMachineStateEventEmitter(routingMachineService);

    const { forward, back, routeWithCondition } = useRouteTransitionActions(
      routingMachineService
    );

    const value = useMemo(() => {
      return { service: routingMachineService, isTransitioning };
    }, [routingMachineService, isTransitioning]);

    return (
      <CartRoutingMachineContext.Provider value={value}>
        {children({
          isTransitioning,
          forward,
          back,
          routeWithCondition,
        })}
      </CartRoutingMachineContext.Provider>
    );
  };
